import React from 'react';
import { InvoiceData, InvoiceSettings } from '../../../types';

interface Props {
  data: InvoiceData;
  settings: InvoiceSettings;
  labels: Record<string, string>;
  subtotal: number;
  taxTotal: number;
  total: number;
}

const StandardTemplate: React.FC<Props> = ({
  data,
  settings,
  labels,
  subtotal,
  taxTotal,
  total,
}) => {
  const currencySymbol = settings.currencySymbol || '$';

  return (
    <div className="p-12 max-w-4xl mx-auto bg-white text-slate-900 min-h-screen relative font-sans animate-in fade-in duration-500">
      <div className="flex justify-between items-start mb-16">
        <div>
          {data.logo ? (
            <img
              key={data.logo.substring(0, 100)} // Force re-render when logo data changes
              src={data.logo}
              alt="Logo"
              className="h-16 w-auto object-contain mb-6 animate-in zoom-in-95 duration-300"
            />
          ) : (
            <div className="w-16 h-16 bg-brand-500 rounded-2xl flex items-center justify-center text-white font-black text-2xl mb-6 shadow-lg shadow-brand-500/20">
              {data.senderName ? data.senderName.charAt(0) : 'IA'}
            </div>
          )}
          <h1 className="text-3xl font-black tracking-tighter mb-2">
            {data.senderName || 'Business Name'}
          </h1>
          <p className="text-sm text-slate-500 whitespace-pre-wrap leading-relaxed max-w-xs">
            {data.senderAddress}
          </p>
        </div>
        <div className="text-right">
          <h2 className="text-5xl font-black text-slate-100 mb-8 uppercase tracking-tighter">
            {labels.invoice}
          </h2>
          <div className="space-y-2 text-sm font-bold">
            <div className="flex justify-between gap-8">
              <span className="text-slate-400 font-medium uppercase text-[10px] tracking-widest">
                Number
              </span>
              <span>{data.number}</span>
            </div>
            <div className="flex justify-between gap-8">
              <span className="text-slate-400 font-medium uppercase text-[10px] tracking-widest">
                Issued
              </span>
              <span>{data.date}</span>
            </div>
            <div className="flex justify-between gap-8">
              <span className="text-slate-400 font-medium uppercase text-[10px] tracking-widest">
                Due
              </span>
              <span>{data.dueDate}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-12 border-l-4 border-brand-500 pl-8 py-2">
        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
          {labels.billTo}
        </h3>
        <p className="text-xl font-black text-slate-900">
          {data.recipientCompany || data.recipientName || 'Client Name'}
        </p>
        <p className="text-slate-500 whitespace-pre-wrap mt-2 text-sm leading-relaxed">
          {data.recipientAddress}
        </p>
      </div>

      <table className="w-full mb-12">
        <thead>
          <tr className="border-b-2 border-slate-900">
            <th className="text-left py-4 font-black text-[10px] uppercase tracking-widest text-slate-400">
              {labels.description}
            </th>
            <th className="text-right py-4 font-black text-[10px] uppercase tracking-widest text-slate-400 w-20">
              {labels.qty}
            </th>
            <th className="text-right py-4 font-black text-[10px] uppercase tracking-widest text-slate-400 w-32">
              {labels.rate}
            </th>
            <th className="text-right py-4 font-black text-[10px] uppercase tracking-widest text-slate-900 w-32">
              {labels.amount}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {data.items.map((item, idx) => (
            <tr key={item.id || idx} className="break-inside-avoid">
              <td className="py-6 font-bold text-slate-800">{item.description}</td>
              <td className="py-6 text-right font-medium text-slate-500">{item.quantity}</td>
              <td className="py-6 text-right font-medium text-slate-500">
                {currencySymbol}
                {item.rate.toFixed(2)}
              </td>
              <td className="py-6 text-right font-black">
                {currencySymbol}
                {(item.quantity * item.rate).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-end pt-8 border-t-2 border-slate-100 break-inside-avoid">
        <div className="w-72 space-y-4">
          <div className="flex justify-between text-slate-500 font-bold text-sm">
            <span>{labels.subtotal}</span>
            <span>
              {currencySymbol}
              {subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between text-slate-500 font-bold text-sm">
            <span>Tax</span>
            <span>
              {currencySymbol}
              {taxTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between text-3xl font-black pt-4 border-t border-slate-100 text-brand-600 tracking-tighter">
            <span>{labels.total}</span>
            <span>
              {currencySymbol}
              {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Payment / Bank Details */}
      {data.paymentInfo && (
        <div className="mt-12 p-6 bg-slate-50 rounded-2xl border border-slate-100 break-inside-avoid">
          <h4 className="font-black text-slate-900 mb-3 uppercase tracking-widest text-[9px]">
            {labels.paymentInfo}
          </h4>
          <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
            {data.paymentInfo}
          </p>
        </div>
      )}

      <div className="mt-auto pt-24 text-xs text-slate-400 break-inside-avoid">
        {data.notes && (
          <div className="mb-6">
            <h4 className="font-black text-slate-900 mb-1 uppercase tracking-widest text-[9px]">
              Notes
            </h4>
            <p className="leading-relaxed">{data.notes}</p>
          </div>
        )}
        <p className="pt-8 border-t border-slate-100 font-medium uppercase text-[8px] tracking-[0.2em]">
          {data.terms}
        </p>
      </div>
    </div>
  );
};

export default StandardTemplate;
